import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../utils/api';

// 异步actions
export const fetchAgents = createAsyncThunk(
  'team/fetchAgents',
  async (params, { rejectWithValue }) => {
    try {
      const response = await api.get('/admin/agents', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '获取代理列表失败');
    }
  }
);

export const fetchAgentDetail = createAsyncThunk(
  'team/fetchAgentDetail',
  async (agentId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/admin/agents/${agentId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '获取代理详情失败');
    }
  }
);

export const fetchAgentTeam = createAsyncThunk(
  'team/fetchAgentTeam',
  async (agentId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/admin/agents/${agentId}/team`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '获取团队信息失败');
    }
  }
);

export const updateAgentLevel = createAsyncThunk(
  'team/updateAgentLevel',
  async ({ agentId, level }, { rejectWithValue }) => {
    try {
      const response = await api.post('/admin/agents/update-level', { agentId, level });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '更新代理等级失败');
    }
  }
);

export const updateAgentStatus = createAsyncThunk(
  'team/updateAgentStatus',
  async ({ agentId, status }, { rejectWithValue }) => {
    try {
      const response = await api.post('/admin/agents/update-status', { agentId, status });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '更新代理状态失败');
    }
  }
);

export const fetchTeamPerformance = createAsyncThunk(
  'team/fetchTeamPerformance',
  async (params, { rejectWithValue }) => {
    try {
      const response = await api.get('/admin/team/performance', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '获取团队业绩失败');
    }
  }
);

export const fetchCommissionRecords = createAsyncThunk(
  'team/fetchCommissionRecords',
  async (params, { rejectWithValue }) => {
    try {
      const response = await api.get('/admin/team/commissions', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '获取返点记录失败');
    }
  }
);

// 返点配置由系统的commission_configs表统一管理
// 不需要单独配置每个代理的返点率

// 初始状态
const initialState = {
  agents: [],
  agentDetail: null,
  agentTeam: [],
  teamPerformance: {
    totalAgents: 0,
    activeAgents: 0,
    totalTeamSize: 0,
    totalRecharge: 0,
    totalCommission: 0,
    averageCommissionRate: 0,
  },
  commissionRecords: [],
  agentPagination: {
    current: 1,
    pageSize: 10,
    total: 0,
  },
  commissionPagination: {
    current: 1,
    pageSize: 10,
    total: 0,
  },
  filters: {},
  isLoading: false,
  error: null,
};

// slice
const teamSlice = createSlice({
  name: 'team',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setFilters: (state, action) => {
      state.filters = action.payload;
    },
    setAgentPagination: (state, action) => {
      state.agentPagination = { ...state.agentPagination, ...action.payload };
    },
    setCommissionPagination: (state, action) => {
      state.commissionPagination = { ...state.commissionPagination, ...action.payload };
    },
    clearAgentDetail: (state) => {
      state.agentDetail = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // 获取代理列表
      .addCase(fetchAgents.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAgents.fulfilled, (state, action) => {
        state.isLoading = false;
        state.agents = action.payload.agents;
        state.agentPagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(fetchAgents.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // 获取代理详情
      .addCase(fetchAgentDetail.fulfilled, (state, action) => {
        state.agentDetail = action.payload;
      })
      // 获取代理团队
      .addCase(fetchAgentTeam.fulfilled, (state, action) => {
        // 确保 agentTeam 始终是数组
        if (Array.isArray(action.payload)) {
          state.agentTeam = action.payload;
        } else if (action.payload && typeof action.payload === 'object') {
          // 如果返回的是对象，尝试提取数组
          state.agentTeam = action.payload.data || action.payload.team || action.payload.children || [];
        } else {
          state.agentTeam = [];
        }
      })
      // 更新代理等级
      .addCase(updateAgentLevel.fulfilled, (state, action) => {
        const index = state.agents.findIndex(agent => agent.id === action.payload.id);
        if (index !== -1) {
          state.agents[index] = action.payload;
        }
        if (state.agentDetail && state.agentDetail.id === action.payload.id) {
          state.agentDetail = action.payload;
        }
      })
      // 更新代理状态
      .addCase(updateAgentStatus.fulfilled, (state, action) => {
        const index = state.agents.findIndex(agent => agent.id === action.payload.id);
        if (index !== -1) {
          state.agents[index] = action.payload;
        }
      })
      // 获取团队业绩
      .addCase(fetchTeamPerformance.fulfilled, (state, action) => {
        state.teamPerformance = action.payload;
      })
      // 获取返点记录
      .addCase(fetchCommissionRecords.fulfilled, (state, action) => {
        state.commissionRecords = action.payload.records;
        state.commissionPagination = action.payload.pagination;
      });
  },
});

export const { clearError, setFilters, setAgentPagination, setCommissionPagination, clearAgentDetail } = teamSlice.actions;
export default teamSlice.reducer;
